/**
 * useStoreCollection
 *
 * Hook for subscribing to a Firestore collection store.
 * Returns state (Valtio proxy) and persist (Firestore API).
 *
 * @example
 * const { state, persist } = useStoreCollection<RowData>('rows');
 * const snap = useSnapshot(state);          // full collection reactivity
 * const rowSnap = useSnapshot(state.docs.get(id)!); // single-row reactivity
 *
 * // Read (from snapshot)
 * const docIds = [...snap.docs.keys()];
 * const cell = rowSnap.data?.cells?.['col1'];
 *
 * // Write (direct Valtio mutation)
 * const mutableDoc = state.docs.get(docId);
 * mutableDoc.data.name = 'New Name';
 *
 * // Save to Firebase
 * persist.update(docId, { name: 'New Name' });
 *
 * // `modifiedAt` is maintained by the store persistence layer.
 *
 * @example Controlled inputs (text fields)
 * // Use sync: true on useSnapshot when the value is bound to a controlled input.
 * // Without sync, the cursor jumps to the end while typing.
 * const rowSnap = useSnapshot(state.docs.get(id)!, { sync: true });
 * <input value={rowSnap.data?.name} onChange={...} />
 */

import { useContext } from 'react';
import { useSnapshot } from 'valtio';
import { StoresContext } from './core/StoresContext';
import { getStoreFromContext } from './core/getStoreFromContext';
import type { CollectionState, CollectionPersist } from './Store.types';

type UseStoreCollectionResult<T> = {
  /** Mutable Valtio state for optimistic updates. */
  state: CollectionState<T>;
  /** Firestore persistence API for collection writes. */
  persist: CollectionPersist<T>;
  /** Valtio useSnapshot, use to subscribe to state changes, mutate state directly. */
  useSnapshot: typeof useSnapshot;
};

/**
 * Get collection state and persist from the store.
 * Returns state (Valtio proxy) and persist (Firestore API).
 * Call `useSnapshot(state)` or `useSnapshot(state.docs.get(id)!)` in the component
 * for granular reactivity.
 *
 * @param storeName - Name of the collection store
 */
export function useStoreCollection<T extends Record<string, unknown>>(
  storeName: string
): UseStoreCollectionResult<T> {
  const ctx = useContext(StoresContext);
  const { state, persist } = getStoreFromContext(ctx, storeName, 'collection');
  const typedState = state as CollectionState<T>;

  return {
    state: typedState,
    persist: persist as CollectionPersist<T>,
    useSnapshot,
  };
}
